import { atom } from 'jotai';
import type { MapRef } from 'react-map-gl';
import { displayValue, isBlank } from './fields';
import type {
  BatchDefault,
  BulkUploadStep,
  CurationRow,
  RowValues,
  ServerRowResult,
  UploadedFile,
  ValidityFilter,
} from './types';

export type {
  BatchDefault,
  BulkUploadStep,
  CurationRow,
  RowValues,
  ServerRowResult,
  UploadedFile,
  ValidityFilter,
} from './types';

/*
 * Curation is browser state: nothing here is persisted until the batch is
 * submitted. The page mounts these atoms inside its own jotai Provider
 * (ProvideRootResource), so every visit starts empty.
 */

/** BulkAssetBatch::MAX_ROWS: the most rows one batch can submit. */
export const MAX_BATCH_ROWS = 500;

export const EMPTY_BATCH_DEFAULT: BatchDefault = {
  entityId: null,
  entityName: null,
  accountId: '',
  accountName: null,
};

export const bulkStepAtom = atom<BulkUploadStep>('entry');
export const curationRowsAtom = atom<CurationRow[]>([]);
export const uploadedFilesAtom = atom<UploadedFile[]>([]);
export const batchDefaultAtom = atom<BatchDefault>(EMPTY_BATCH_DEFAULT);
export const tableSearchAtom = atom('');
export const validityFilterAtom = atom<ValidityFilter>('all');
export const showMapAtom = atom(false);
/** The inline map's own ref (not the app-wide one): the page flies it to rows. */
export const bulkMapRefAtom = atom<MapRef | null>(null);
/** The row the map is on; a new object on every View asset on map. */
export const mapFocusAtom = atom<{ key: string } | null>(null);

/** View asset on map: opens the map and flies to the row, again if re-chosen. */
export const focusMapRowAtom = atom(null, (_get, set, key: string) => {
  set(showMapAtom, true);
  set(mapFocusAtom, { key });
});
/** The last dry-run failure, shown until the next attempt. */
export const dryRunErrorAtom = atom<string | null>(null);
/** Bumped to re-send the pending rows after a failed dry-run. */
export const dryRunRetryAtom = atom(0);

let lastClientId = 0;
const nextClientId = () => `row-${(lastClientId += 1)}`;

const renumber = (rows: CurationRow[]) =>
  rows.map((row, index) => ({ ...row, position: index + 1 }));

export const isRowValid = (row: CurationRow) =>
  !row.pending && !!row.result?.valid;

/**
 * A row's kept "was ignored" warnings after an answer: the answer's own added
 * while the row keeps its interest type, as a re-validation cannot repeat a
 * warning for a value already gone. A new type takes only the answer's own,
 * so every warning names the type the row has.
 * @param {CurationRow} row - The row before the answer
 * @param {ServerRowResult} result - The answer
 * @return {CurationRow['ignored']} The warnings to keep
 */
const keepIgnored = (
  row: CurationRow,
  result: ServerRowResult
): CurationRow['ignored'] =>
  row.result?.values.instrumentType === result.values.instrumentType
    ? { ...row.ignored, ...result.ignored }
    : result.ignored;

export const isBatchDefaultSet = ({ entityId, accountId }: BatchDefault) =>
  entityId !== null && accountId !== '';

export const validityCountsAtom = atom((get) => {
  const rows = get(curationRowsAtom);
  const valid = rows.filter(isRowValid).length;
  const pending = rows.filter((row) => row.pending).length;
  return {
    all: rows.length,
    valid,
    invalid: rows.length - valid - pending,
    pending,
  };
});

/**
 * The rows the table shows: the validity filter, then the search across every
 * field's display text. Rows still being validated count as neither.
 */
export const filteredRowsAtom = atom((get) => {
  const filter = get(validityFilterAtom);
  const term = get(tableSearchAtom).trim().toLowerCase();
  return get(curationRowsAtom).filter((row) => {
    if (filter === 'valid' && !isRowValid(row)) return false;
    if (filter === 'invalid' && (row.pending || isRowValid(row))) return false;
    if (!term) return true;
    return Object.values(row.values).some(
      (value) =>
        !isBlank(value) &&
        [String(value), displayValue(value)].some((text) =>
          text.toLowerCase().includes(term)
        )
    );
  });
});

/** Next is available with a batch default, one valid row, and nothing in flight. */
export const canProceedAtom = atom((get) => {
  const counts = get(validityCountsAtom);
  return (
    isBatchDefaultSet(get(batchDefaultAtom)) &&
    counts.valid > 0 &&
    counts.pending === 0 &&
    counts.all <= MAX_BATCH_ROWS &&
    !get(uploadedFilesAtom).some((file) => file.status === 'uploading')
  );
});

/** Appends one upload's rows. `stale` rows are re-validated (the default moved). */
export const appendUploadedRowsAtom = atom(
  null,
  (
    get,
    set,
    {
      fileId,
      results,
      stale,
    }: { fileId: string; results: ServerRowResult[]; stale: boolean }
  ) => {
    const rows = get(curationRowsAtom);
    const added = results.map((result, index): CurationRow => ({
      clientId: nextClientId(),
      position: rows.length + index + 1,
      fileId,
      values: result.values,
      result,
      ignored: result.ignored,
      pending: stale,
      revision: 0,
    }));
    set(curationRowsAtom, [...rows, ...added]);
  }
);

export const addRowAtom = atom(null, (get, set, values: RowValues) => {
  const rows = get(curationRowsAtom);
  set(curationRowsAtom, [
    ...rows,
    {
      clientId: nextClientId(),
      position: rows.length + 1,
      fileId: null,
      values,
      result: null,
      ignored: {},
      pending: true,
      revision: 0,
    },
  ]);
});

export const updateRowAtom = atom(
  null,
  (get, set, { clientId, values }: { clientId: string; values: RowValues }) => {
    set(
      curationRowsAtom,
      get(curationRowsAtom).map((row) => {
        if (row.clientId !== clientId) return row;
        return { ...row, values, pending: true, revision: row.revision + 1 };
      })
    );
  }
);

export const removeRowAtom = atom(null, (get, set, clientId: string) => {
  set(
    curationRowsAtom,
    renumber(get(curationRowsAtom).filter((row) => row.clientId !== clientId))
  );
});

/** Drops a listed file and the rows it brought in; other rows stay. */
export const removeFileAtom = atom(null, (get, set, fileId: string) => {
  set(
    uploadedFilesAtom,
    get(uploadedFilesAtom).filter((file) => file.id !== fileId)
  );
  set(
    curationRowsAtom,
    renumber(get(curationRowsAtom).filter((row) => row.fileId !== fileId))
  );
});

/** Every row resolves against the default, so a new default re-validates all. */
export const setBatchDefaultAtom = atom(
  null,
  (get, set, next: BatchDefault) => {
    set(batchDefaultAtom, next);
    set(
      curationRowsAtom,
      get(curationRowsAtom).map((row) => ({
        ...row,
        pending: true,
        revision: row.revision + 1,
      }))
    );
  }
);

/**
 * Applies a dry-run answer. `sent` maps each request position to the row and
 * revision it carried; an answer for values that have since changed is dropped,
 * and that row's newer request answers for it.
 */
export const applyDryRunAtom = atom(
  null,
  (
    get,
    set,
    {
      sent,
      results,
    }: {
      sent: Map<number, { clientId: string; revision: number }>;
      results: ServerRowResult[];
    }
  ) => {
    const byClientId = new Map(
      results.flatMap((result) => {
        const request = sent.get(result.position);
        return request
          ? [[request.clientId, { request, result }] as const]
          : [];
      })
    );
    set(
      curationRowsAtom,
      get(curationRowsAtom).map((row) => {
        const answer = byClientId.get(row.clientId);
        if (!answer || answer.request.revision !== row.revision) return row;
        return {
          ...row,
          values: answer.result.values,
          result: answer.result,
          ignored: keepIgnored(row, answer.result),
          pending: false,
        };
      })
    );
  }
);

/** Marks the rows a rejected submit named (by position) Invalid with its errors. */
export const markRowErrorsAtom = atom(
  null,
  (
    get,
    set,
    rejected: Array<{ position: number; errors: ServerRowResult['errors'] }>
  ) => {
    const byPosition = new Map(
      rejected.map(({ position, errors }) => [position, errors])
    );
    set(
      curationRowsAtom,
      get(curationRowsAtom).map((row) => {
        const errors = byPosition.get(row.position);
        if (!errors || !row.result) return row;
        return { ...row, result: { ...row.result, errors, valid: false } };
      })
    );
  }
);
